import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useSetWorkspaceStatus, useDeleteWorkspace } from "@/lib/coachQueries";
import { useMeetingConfig, useSaveMeetingConfig, detailMessage } from "@/lib/zoomQueries";
import { useSetSharedPresentation } from "@/lib/teamWorkQueries";
import { useTeamMembers } from "@/lib/teamRecordQueries";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Video, Presentation as PresentationIcon, AlertTriangle } from "lucide-react";
import { TypeToConfirmDialog } from "@/pages/RehearsalRoom/shared/TypeToConfirmDialog";

const TRANSITIONS = {
  draft: ["paused", "completed", "archived"], active: ["paused", "completed", "archived"],
  paused: ["draft", "completed", "archived"], completed: ["draft", "archived"], archived: [],
};
const STATUS_LABELS = { draft: "Resume to Active", paused: "Pause", completed: "Mark Completed", archived: "Archive" };

/**
 * Clients Restructure — Team 360 -> Access & Settings. Team-level
 * settings only (lifecycle, Shared Presentation, Zoom meeting config,
 * Delete Engagement) + the roster — never an individual member's own
 * account settings (that's their own Client 360's Access & Settings).
 */
export default function AccessSettingsTab({ workspaceId, overview }) {
  const navigate = useNavigate();
  const { data: membersData } = useTeamMembers(workspaceId);
  const setStatus = useSetWorkspaceStatus(workspaceId);
  const setSharedPresentation = useSetSharedPresentation(workspaceId);
  const { data: meetingConfig } = useMeetingConfig(workspaceId);
  const saveMeetingConfig = useSaveMeetingConfig(workspaceId);
  const [meetingNumber, setMeetingNumber] = useState("");
  const [meetingPassword, setMeetingPassword] = useState("");
  const [hostZoomUserId, setHostZoomUserId] = useState("");
  const [showDeleteWorkspace, setShowDeleteWorkspace] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const deleteWorkspace = useDeleteWorkspace();

  useEffect(() => {
    if (meetingConfig?.configured) {
      setMeetingNumber(meetingConfig.zoom_meeting_number || "");
      setHostZoomUserId(meetingConfig.zoom_host_zoom_user_id || "");
    }
  }, [meetingConfig]);

  async function handleSaveMeetingConfig() {
    try {
      await saveMeetingConfig.mutateAsync({
        zoom_meeting_number: meetingNumber, zoom_meeting_password: meetingPassword || null, zoom_host_zoom_user_id: hostZoomUserId,
      });
      toast.success("Zoom meeting settings saved");
      setMeetingPassword("");
    } catch (err) {
      toast.error(detailMessage(err.body, "Couldn't save the meeting settings"));
    }
  }

  async function handleToggleSharedPresentation(enabled) {
    try {
      await setSharedPresentation.mutateAsync(enabled);
      toast.success(enabled ? "Shared Presentation enabled for the team" : "Shared Presentation disabled");
    } catch (err) {
      toast.error(detailMessage(err.body, "Couldn't update Shared Presentation"));
    }
  }

  const allowedNext = TRANSITIONS[overview.status] || [];

  async function handleTransition(newStatus) {
    try {
      await setStatus.mutateAsync({ new_status: newStatus });
      toast.success(`Workspace moved to ${newStatus}`);
    } catch (err) {
      toast.error(err.message === "request_failed" ? "Couldn't change status" : err.message);
    }
  }

  async function handleDeleteWorkspace() {
    setDeleteError(null);
    try {
      await deleteWorkspace.mutateAsync({ workspaceId, confirmTitle: overview.team_name });
      toast.success(`"${overview.team_name}" was permanently deleted`);
      setShowDeleteWorkspace(false);
      navigate("/rehearsal-room/coach/clients");
    } catch (err) {
      setDeleteError(err.body?.detail || "Couldn't delete this Engagement");
    }
  }

  return (
    <div data-testid="team-360-access-settings-tab" className="space-y-6 max-w-lg">
      <div className="border border-ink/10 rounded-lg p-4 bg-white/50">
        <h2 className="font-serif text-lg text-ink mb-2">Team members</h2>
        <ul className="space-y-1" data-testid="team-access-members-list">
          {(membersData?.items || []).map((m) => (
            <li key={m.member_id} className="text-sm text-ink">{m.name}</li>
          ))}
        </ul>
      </div>

      <div className="border border-ink/10 rounded-lg p-4 bg-white/50">
        <h2 className="font-serif text-lg text-ink mb-2">Workspace lifecycle</h2>
        <p className="text-xs text-ink-soft mb-3">Current status: <span className="font-medium text-ink uppercase">{overview.status}</span></p>
        <div className="flex flex-wrap gap-2">
          {allowedNext.length === 0 ? (
            <p className="text-sm text-ink-soft" data-testid="team-access-lifecycle-terminal">Archived is a terminal state.</p>
          ) : (
            allowedNext.map((s) => (
              <button
                key={s}
                onClick={() => handleTransition(s)}
                disabled={setStatus.isPending}
                className="text-sm font-medium border border-ink/15 rounded-full px-4 py-1.5 text-ink hover:bg-ink/5 transition-colors disabled:opacity-50"
                data-testid={`team-access-lifecycle-${s}`}
              >
                {STATUS_LABELS[s]}
              </button>
            ))
          )}
        </div>
      </div>

      <div className="border border-ink/10 rounded-lg p-4 bg-white/50" data-testid="team-access-shared-presentation-card">
        <h2 className="font-serif text-lg text-ink mb-2 flex items-center gap-2"><PresentationIcon size={16} /> Shared Presentation</h2>
        <p className="text-xs text-ink-soft mb-3">
          When on, this team sees an extra "Presentation" destination in their sidebar and a shared Presentation
          surfaces in Our Work — never any one member's private draft.
        </p>
        <div className="flex items-center gap-3">
          <Switch
            checked={!!overview.shared_presentation_enabled}
            onCheckedChange={handleToggleSharedPresentation}
            disabled={setSharedPresentation.isPending}
            data-testid="team-access-shared-presentation-toggle"
          />
          <span className="text-sm text-ink">{overview.shared_presentation_enabled ? "Enabled" : "Disabled"}</span>
        </div>
      </div>

      <div className="border border-ink/10 rounded-lg p-4 bg-white/50" data-testid="team-access-zoom-meeting-config">
        <h2 className="font-serif text-lg text-ink mb-2 flex items-center gap-2"><Video size={16} /> Zoom Meeting</h2>
        <div className="space-y-2">
          <div>
            <label className="text-xs text-ink-soft mb-1 block">Meeting number (Personal Meeting ID)</label>
            <Input value={meetingNumber} onChange={(e) => setMeetingNumber(e.target.value)} placeholder="123456789" data-testid="team-zoom-config-meeting-number-input" />
          </div>
          <div>
            <label className="text-xs text-ink-soft mb-1 block">
              Meeting password {meetingConfig?.has_password && <span className="text-ink/40">(already set — leave blank to keep it)</span>}
            </label>
            <Input type="password" value={meetingPassword} onChange={(e) => setMeetingPassword(e.target.value)} placeholder={meetingConfig?.has_password ? "••••••••" : "Optional"} data-testid="team-zoom-config-meeting-password-input" />
          </div>
          <div>
            <label className="text-xs text-ink-soft mb-1 block">Host Zoom account (email)</label>
            <Input value={hostZoomUserId} onChange={(e) => setHostZoomUserId(e.target.value)} placeholder="coach@example.com" data-testid="team-zoom-config-host-user-id-input" />
          </div>
          <button
            onClick={handleSaveMeetingConfig}
            disabled={saveMeetingConfig.isPending || !meetingNumber || !hostZoomUserId}
            className="text-sm font-medium border border-ink/15 rounded-full px-4 py-1.5 text-ink hover:bg-ink/5 transition-colors disabled:opacity-50"
            data-testid="team-zoom-config-save-button"
          >
            Save
          </button>
        </div>
      </div>

      <div className="border border-red-200 rounded-lg p-4 bg-red-50/50" data-testid="team-access-danger-zone">
        <h2 className="font-serif text-lg text-destructive mb-2 flex items-center gap-2"><AlertTriangle size={16} /> Danger zone</h2>
        <p className="text-xs text-ink-soft mb-3">Permanent. Requires typing the exact team name to confirm.</p>
        <button
          onClick={() => setShowDeleteWorkspace(true)}
          className="text-sm font-medium border border-red-200 rounded-full px-4 py-1.5 text-destructive hover:bg-red-100 transition-colors"
          data-testid="team-access-delete-workspace-open"
        >
          Delete this Engagement
        </button>
      </div>

      <TypeToConfirmDialog
        open={showDeleteWorkspace} onOpenChange={(o) => { setShowDeleteWorkspace(o); if (!o) setDeleteError(null); }}
        title={`Delete "${overview.team_name}"?`}
        description="This removes the entire Engagement — every member, session, file, presentation, feedback, message and contract/invoice tied to it. This can't be undone."
        confirmValue={overview.team_name}
        onConfirm={handleDeleteWorkspace} isPending={deleteWorkspace.isPending} error={deleteError}
        testId="team-access-delete-workspace-dialog"
      />
    </div>
  );
}
